import Icon from '@/components/core/Icon/Icon';
import Typography from '@/components/core/Typography';
import Logo from '@/components/Logo/Logo';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

const Root = styled.div`
    background: #5f90cd;
    background: radial-gradient(146.17% 139.96% at 100% 100%, #4475B3 0%, #4475B3 43.22%, #7CADEB 100%);
    border-radius: 6px;
    width: 446px;
    min-height: 658px;
    height: 100%;
    padding: 24px;
    overflow: hidden;
    display: grid;
    justify-items: center;
    position: relative;
    grid-template-rows: auto 1fr auto;
    align-content: space-between;

    @media (max-height: 750px) {
        & > div:last-child {
            display: none;
        }
    }
`;

const CircleBg = styled.div<{
    $toSlide: number
}>`
    position: absolute;
    right: 111px;
    bottom: -110px;
    width: 552px;
    height: 552px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.palette.white};
    opacity: 0.1;
    z-index: 0;
    transition: transform 1.2s ease-in-out;

    ${({ $toSlide }) => $toSlide === 1 && css`
        transform: translate(40%, -12%) scale(0.8);
    `}

    ${({ $toSlide }) => $toSlide === 2 && css`
        transform: translate(35%, -23%) scale(1.01);
    `}
`;

const TopSection = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: space-between;
    align-items: start; 
    gap: 12px;
`;

const StyledLogo = styled(Logo)`
    background-color: ${({ theme }) => theme.colors.palette.white};
`;

const HeadingWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const BannerPrimaryTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.palette.white};
`;

const BannerSecondaryTypography = styled(BannerPrimaryTypography)`
    opacity: 80%;
    white-space: pre-wrap;
`;

const CreateAccountTypography = styled(BannerSecondaryTypography)`
    line-height: 21px;
`;

const SlidingContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
`;

const SlidingContent = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 530px;
`;

const Slide = styled.div<{
    $zIndex: number;
    $left?: number;
    $right?: number;
    $top?: number;
    $bottom?: number;
    $showing?: boolean;
    $hiding?: boolean;
    $isIdle?: boolean;
}>`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 18px;
    position: absolute;
    z-index: 1;
    left: ${({ $left }) => $left}px;
    right: ${({ $right }) => $right}px;
    top: ${({ $top }) => $top}px;
    bottom: ${({ $bottom }) => $bottom}px;
    transition: transform 1.2s ease-in-out;

    transform: translateX(-150%);

    ${({ $showing, $isIdle }) => $showing && !$isIdle && css`
        z-index: 2;
        transform: translateX(0);
    `}

    ${({ $isIdle }) => $isIdle && css`
        transform: translateX(0);
    `}
`;

const SlideRight = styled(Slide)<{
    $textAlignLeft?: boolean;
}>`
    align-items: ${({ $textAlignLeft }) => $textAlignLeft ? 'flex-start' : 'flex-end'};
    z-index: 4;
    transform: translateX(150%);

    ${({ $showing, $isIdle }) => $showing && !$isIdle && css`
        z-index: 3;
        transform: translateX(0);
    `}

    ${({ $isIdle }) => $isIdle && css`
        transform: translateX(0);
    `}
`;

const SlideDescription = styled.div<{
    $alignment: 'left' | 'right';
}>`
    max-width: 370px;
    display: flex;
    flex-direction: column;
    flex-wrap: ${({ $alignment }) => $alignment === 'left' ? 'auto' : 'wrap-reverse'};
    text-align: ${({ $alignment }) => $alignment};
`;

const StyledIcon = styled(Icon)`
    color: ${({ theme }) => theme.colors.palette.white};
    display: block;
`;

const SlideImg = styled.img<{
    $height: number;
    $width: number;
    $noRightBorderRadius?: boolean;
}>`
    height: ${({ $height }) => $height}px;
    width: ${({ $width }) => $width}px;
    box-shadow: 0px 6px 6px 0px rgba(0, 0, 0, 0.16), 0px 0px 1px 0px rgba(0, 0, 0, 0.40);
    border-radius: ${({ $noRightBorderRadius }) => $noRightBorderRadius ? '6px 0 0 6px' : '6px'};

    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
`;

const SlideImgWrapper = styled.div`
    position: relative;
`;

const SlideImgTooltip = styled(SlideImg)`
    position: absolute;
    bottom: -95px;
    right: 0;
    box-shadow: none;
`;

const SliderButtons = styled.div`
    display: flex;
    gap: 12px;
    height: 24px;
    align-self: flex-end;
    align-items: center;
    z-index: 2;
`;

const BulletButton = styled.div<{
    $active?: boolean;
}>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.palette.blue_300};
    cursor: pointer;
    ${({ theme, $active }) => $active && `background-color: ${theme.colors.palette.blue_700};`};
    transition: background-color 1.2s ease-in-out;
`;

export function Banner() {
    const [slide, setSlide] = useState({
        from: 0,
        to: 0
    });

    const t = useTranslations();

    useEffect(() => {
        const interval = setInterval(() => {
            setSlide({
                from: slide.to,
                to: slide.to === 2 ? 0 : slide.to + 1
            });
        }, 6000);
        return () => clearInterval(interval);
    }, [slide.to]);

    const isIdle = slide.from === slide.to;

    return (
        <Root>
            <CircleBg $toSlide={slide.to}>&nbsp;</CircleBg>
            <TopSection>
                <StyledLogo width='102px' height='36px' $withMask />
                <HeadingWrapper>
                    <BannerPrimaryTypography variant='uiType' size='2xl' modifier='S'>
                        {t('auth.getStarted')}
                    </BannerPrimaryTypography>
                    <CreateAccountTypography variant='uiType' size='sm' modifier='S'>
                        {t('auth.createYourAccountInMinutes')}
                    </CreateAccountTypography>
                </HeadingWrapper>
            </TopSection>
            <SlidingContentWrapper>
                <SlidingContent>
                    <SlideRight
                      $right={0}
                      $zIndex={1}
                      $top={18}
                      $showing={slide.to === 0}
                      $hiding={slide.from === 0}
                      $isIdle={isIdle && slide.to === 0}
                    >
                        <SlideDescription $alignment='right'>
                            <StyledIcon name='chartbar-2-light' />
                            <BannerPrimaryTypography variant='uiType' size='sm' modifier='S'>
                                {t('auth.bannerSlides.realTimeInsights.title')}
                            </BannerPrimaryTypography>
                            <BannerSecondaryTypography variant='uiType' size='xs' modifier='R'>
                                {t('auth.bannerSlides.realTimeInsights.description')}
                            </BannerSecondaryTypography>
                        </SlideDescription>
                        <SlideImg src='/img/sign-in-banner/chart-block-slide.png' $height={235} $width={684} draggable={false} />
                    </SlideRight>
                    <Slide
                      $left={34}
                      $zIndex={0}
                      $top={116}
                      $showing={slide.to === 0}
                      $hiding={slide.from === 0}
                      $isIdle={isIdle && slide.to === 0}
                    >
                        <SlideImg src='/img/sign-in-banner/table-block-slide.png' $height={296} $width={793} draggable={false} />
                        <SlideDescription $alignment='left'>
                            <StyledIcon name='diagnostic' />
                            <BannerPrimaryTypography variant='uiType' size='sm' modifier='S'>
                                {t('auth.bannerSlides.fullDataAtGlance.title')}
                            </BannerPrimaryTypography>
                            <BannerSecondaryTypography variant='uiType' size='xs' modifier='R'>
                                {t('auth.bannerSlides.fullDataAtGlance.description')}
                            </BannerSecondaryTypography>
                        </SlideDescription>
                    </Slide>

                    <SlideRight
                      $right={0}
                      $zIndex={1}
                      $top={18}
                      $showing={slide.to === 1}
                      $hiding={slide.from === 1}
                      $isIdle={isIdle && slide.to === 1}
                    >
                        <SlideDescription $alignment='right'>
                            <StyledIcon name='user-light' />
                            <BannerPrimaryTypography variant='uiType' size='sm' modifier='S'>
                                {t('auth.bannerSlides.clientManagement.title')}
                            </BannerPrimaryTypography>
                            <BannerSecondaryTypography variant='uiType' size='xs' modifier='R'>
                                {t('auth.bannerSlides.clientManagement.description')}
                            </BannerSecondaryTypography>
                        </SlideDescription>
                        <SlideImgWrapper>
                            <SlideImg src='/img/sign-in-banner/client-management-block-slide.png' $height={243} $width={273} draggable={false} />
                            <SlideImgTooltip src='/img/sign-in-banner/client-management-block-slide-tooltip.png' $height={116} $width={114} />
                        </SlideImgWrapper>
                    </SlideRight>
                    <Slide
                      $left={0}
                      $zIndex={0}
                      $top={187}
                      $showing={slide.to === 1}
                      $hiding={slide.from === 1}
                      $isIdle={isIdle && slide.to === 1}
                    >
                        <SlideImg src='/img/sign-in-banner/staff-block-slide.png' $height={223} $width={380} draggable={false} />
                        <SlideDescription $alignment='left'>
                            <StyledIcon name='staff' />
                            <BannerPrimaryTypography variant='uiType' size='sm' modifier='S'>
                                {t('auth.bannerSlides.staffOverview.title')}
                            </BannerPrimaryTypography>
                            <BannerSecondaryTypography variant='uiType' size='xs' modifier='R'>
                                {t('auth.bannerSlides.staffOverview.description')}
                            </BannerSecondaryTypography>
                        </SlideDescription>
                    </Slide>

                    <SlideRight
                      $right={-24}
                      $zIndex={1}
                      $top={18}
                      $showing={slide.to === 2}
                      $hiding={slide.from === 2}
                      $isIdle={isIdle && slide.to === 2}
                      $textAlignLeft
                    >
                        <SlideDescription $alignment='left'>
                            <StyledIcon name='task' />
                            <BannerPrimaryTypography variant='uiType' size='sm' modifier='S'>
                                {t('auth.bannerSlides.taskManager.title')}
                            </BannerPrimaryTypography>
                            <BannerSecondaryTypography variant='uiType' size='xs' modifier='R'>
                                {t('auth.bannerSlides.taskManager.description')}
                            </BannerSecondaryTypography>
                        </SlideDescription>
                        <SlideImg src='/img/sign-in-banner/task-manager-block-slide.png' $height={407} $width={423} draggable={false} $noRightBorderRadius />
                    </SlideRight>
                </SlidingContent>
            </SlidingContentWrapper>
            <SliderButtons>
                {
                    [...Array(3)].map((_, index) => (
                        <BulletButton
                          onClick={() => setSlide((prev) => {
                              return {
                                  from: prev.to,
                                  to: index
                              };
                          })}
                          $active={index == slide.to}
                          key={index}
                        />
                    ))
                }
            </SliderButtons>
        </Root>
    );
}
